import Badge from './ui/Badge';
import Button from './ui/Button';
import Card from './ui/Card';
import { PROMO } from '../config/promo';
import { cn } from '../lib/utils';

export function CheckIcon({ className }) {
    return (
        <svg
            className={cn('h-4 w-4 shrink-0', className)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M5 13l4 4L19 7" />
        </svg>
    );
}

export function ScreenBadge({ count }) {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-2.5 py-1 text-xs font-semibold text-content-muted">
            <svg
                className="h-3.5 w-3.5 text-brand"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
            >
                <rect x="2" y="4" width="20" height="13" rx="2" />
                <path d="M8 21h8" strokeLinecap="round" />
            </svg>
            {count} écran{count > 1 ? 's' : ''} simultané{count > 1 ? 's' : ''}
        </span>
    );
}

/**
 * Subscription plan card, shared by the pricing section and the season landing
 * page so both always render the same prices, links and layout.
 */
export default function PlanCard({ plan, className }) {
    return (
        <Card
            as="article"
            interactive
            highlighted={plan.isPopular}
            className={cn('flex h-full flex-col p-6 sm:p-7', plan.isPopular && 'ring-1 ring-brand/30', className)}
        >
            {plan.badge && (
                <Badge tone={plan.isPopular ? 'solid' : 'brand'} className="absolute -top-3 left-6">
                    {plan.badge}
                </Badge>
            )}

            <header className={cn(plan.badge && 'mt-2')}>
                <h3 className="text-title font-bold text-white">{plan.name}</h3>
                <p className="mt-1 text-sm text-content-subtle">Paiement unique · sans engagement</p>
            </header>

            <div className="mt-6">
                <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-extrabold tracking-tight text-white">
                        {plan.price}€
                    </span>
                    <span className="text-sm text-content-muted">/ {plan.duration}</span>
                </div>
                {plan.originalPrice && (
                    <p className="mt-1.5 flex items-center gap-2 text-sm">
                        <span className="text-content-subtle line-through">{plan.originalPrice}€</span>
                        <span className="font-semibold text-brand">
                            Économisez {PROMO.discountEur}€
                        </span>
                    </p>
                )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <ScreenBadge count={plan.screens} />
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
                    Lecteur IBO Pro offert
                </span>
            </div>

            <ul className="mt-7 flex-1 space-y-3 border-t border-line pt-6">
                {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-3 text-sm leading-relaxed text-content-muted">
                        <CheckIcon className="mt-0.5 text-brand" />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-7">
                <Button
                    href={plan.link}
                    variant={plan.isPopular ? 'primary' : 'secondary'}
                    fullWidth
                    size="lg"
                >
                    Choisir ce forfait
                </Button>
                <p className="mt-3 text-center text-xs leading-relaxed text-content-subtle">
                    Saisissez votre adresse e-mail à la commande : vos codes d'accès et le guide
                    d'installation y sont envoyés.
                </p>
            </div>
        </Card>
    );
}
